export type ParsedPdfExercise = {
  name: string;
  sets: string;
  reps: string;
  load: string;
  interval: string;
  observation: string;
};

export type ParsedPdfPrescription = {
  name: string;
  observation: string;
  exercises: ParsedPdfExercise[];
};

export type ParsedWorkoutPdf = {
  planName: string;
  meta: string[];
  prescriptions: ParsedPdfPrescription[];
};

const FIELD_RE =
  /^(s[ée]ries|carga|intervalo|instru[cç][oõ]es)\s*:\s*(.*)$/i;
const SERIES_BELOW_RE = /s[ée]ries?\s+abaixo/i;
const SETS_REPS_RE = /(\d+)\s*[xX×]\s*(\d+(?:\s*a\s*\d+)?)/;
const ROTINA_RE = /^rotina\s*:\s*(.+)$/i;
const PAGE_MARK_RE = /^--\s*\d+\s*of\s*\d+\s*--$/i;
const META_KEYWORDS = new Set([
  "hipertrofia",
  "emagrecimento",
  "força",
  "forca",
  "resistência",
  "resistencia",
  "iniciante",
  "intermediário",
  "intermediario",
  "avançado",
  "avancado",
]);

function cleanLines(text: string): string[] {
  return text
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((l) =>
      l
        // ícones/PUA do PDF de apps de treino
        .replace(/^[\uE000-\uF8FF\uFE00-\uFE0F\u200B-\u200D\uFEFF]+/g, "")
        .replace(/[^\S\n]+/g, " ")
        .trim(),
    )
    .filter((l) => l.length > 0 && !PAGE_MARK_RE.test(l));
}

function parseSetsReps(
  seriesValue: string,
  instructions: string,
): { sets: string; reps: string } {
  const fromSeries = seriesValue.match(SETS_REPS_RE);
  if (fromSeries && !SERIES_BELOW_RE.test(seriesValue)) {
    return {
      sets: String(Number(fromSeries[1])),
      reps: fromSeries[2].replace(/\s+/g, ""),
    };
  }
  const fromInstr = instructions.match(SETS_REPS_RE);
  if (fromInstr) {
    return {
      sets: String(Number(fromInstr[1])),
      reps: fromInstr[2].replace(/\s+/g, ""),
    };
  }
  return { sets: "", reps: "" };
}

function isFieldLine(line: string): boolean {
  return FIELD_RE.test(line);
}

function nextSeriesNameIndex(lines: string[], from: number): number {
  // Só considera título de exercício se a próxima linha útil for "Séries:"
  for (let j = from + 1; j < Math.min(from + 3, lines.length); j += 1) {
    if (/^(s[ée]ries)\s*:/i.test(lines[j])) return j;
    // Qualquer outra linha (campo ou texto) interrompe
    return -1;
  }
  return -1;
}

function isExerciseTitle(lines: string[], index: number): boolean {
  return nextSeriesNameIndex(lines, index) >= 0;
}

/** Evita tratar frases de instrução como novo grupo muscular. */
function looksLikeSectionHeader(line: string): boolean {
  const lower = line.toLowerCase();
  if (
    /^(realize|fa[cç]a|descansa|sendo que|na [uú]ltima|furo:|declinado|reps\.?$)/i.test(
      lower,
    )
  ) {
    return false;
  }
  if (/^\d/.test(line)) return false;
  if (SETS_REPS_RE.test(line)) return false;
  if (/[.!?]$/.test(line)) return false;
  const words = line.split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > 6) return false;
  return true;
}

type DraftExercise = {
  name: string;
  seriesRaw: string;
  load: string;
  interval: string;
  instructionLines: string[];
};

/**
 * Parseia texto extraído de PDF de apps de treino (ex.: export com Rotina/Séries/Carga).
 * Função pura — O(n) nas linhas.
 */
export function parseWorkoutPdfText(text: string): ParsedWorkoutPdf {
  if (!text || typeof text !== "string") {
    return { planName: "Plano importado", meta: [], prescriptions: [] };
  }

  const lines = cleanLines(text);
  let planName = "Plano importado";
  const meta: string[] = [];

  for (const line of lines) {
    const rotina = line.match(ROTINA_RE);
    if (rotina) {
      planName = rotina[1].trim();
      break;
    }
  }

  const prescriptions: ParsedPdfPrescription[] = [];
  let currentPrescription: ParsedPdfPrescription | null = null;
  let currentExercise: DraftExercise | null = null;
  let inInstructions = false;
  let passedHeader = false;

  const flushExercise = () => {
    if (!currentExercise || !currentPrescription) return;
    const observation = currentExercise.instructionLines.join("\n").trim();
    const { sets, reps } = parseSetsReps(
      currentExercise.seriesRaw,
      observation,
    );
    currentPrescription.exercises.push({
      name: currentExercise.name,
      sets,
      reps,
      load: currentExercise.load,
      interval: currentExercise.interval.replace(/s$/i, "").trim(),
      observation,
    });
    currentExercise = null;
    inInstructions = false;
  };

  const ensurePrescription = (name: string) => {
    flushExercise();
    currentPrescription = {
      name:
        name.trim() ||
        `Treino ${String.fromCodePoint(65 + prescriptions.length)}`,
      observation: meta.join(" · "),
      exercises: [],
    };
    prescriptions.push(currentPrescription);
  };

  lines.forEach((line, index) => {
    if (ROTINA_RE.test(line)) return;

    if (!passedHeader) {
      if (index === 0 && !META_KEYWORDS.has(line.toLowerCase())) {
        return; // nome do aluno
      }
      if (META_KEYWORDS.has(line.toLowerCase())) {
        meta.push(line);
        return;
      }
    }

    const field = line.match(FIELD_RE);
    if (field && currentExercise) {
      passedHeader = true;
      const key = field[1]
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{M}/gu, "");
      const value = (field[2] || "").trim();
      if (key.startsWith("serie")) {
        currentExercise.seriesRaw = value;
        inInstructions = false;
      } else if (key.startsWith("carga")) {
        currentExercise.load = value;
        inInstructions = false;
      } else if (key.startsWith("intervalo")) {
        currentExercise.interval = value;
        inInstructions = false;
      } else if (key.startsWith("instruc")) {
        inInstructions = true;
        if (value) currentExercise.instructionLines.push(value);
      }
      return;
    }

    // Novo exercício (nome seguido de Séries:)
    if (isExerciseTitle(lines, index)) {
      passedHeader = true;
      flushExercise();
      if (!currentPrescription) {
        ensurePrescription("Treino A");
      }
      currentExercise = {
        name: line,
        seriesRaw: "",
        load: "",
        interval: "",
        instructionLines: [],
      };
      inInstructions = false;
      return;
    }

    // Continuação de instruções — ou cabeçalho do próximo treino
    if (inInstructions && currentExercise) {
      if (
        looksLikeSectionHeader(line) &&
        index + 1 < lines.length &&
        isExerciseTitle(lines, index + 1)
      ) {
        flushExercise();
        ensurePrescription(line);
        return;
      }
      currentExercise.instructionLines.push(line);
      return;
    }

    // Cabeçalho de grupo muscular / treino (nunca labels de campo)
    if (
      !isFieldLine(line) &&
      !currentExercise &&
      !/^(s[ée]ries|carga|intervalo|instru[cç][oõ]es)\b/i.test(line) &&
      looksLikeSectionHeader(line)
    ) {
      passedHeader = true;
      ensurePrescription(line);
    }
  });

  flushExercise();

  const nonEmpty = prescriptions.filter((p) => p.exercises.length > 0);
  return {
    planName,
    meta,
    prescriptions: nonEmpty.length > 0 ? nonEmpty : prescriptions,
  };
}
